import Constant from '@/common/constant/Constant';
import Utils from '@/common/utils/utils';
import { cloneDeep } from 'lodash';
import { getTicketListOfCustomerUser, archiveTicketList } from '@/service/ticketService';
import { handleRestoreUser, updateUserResource, updateB2CUserListInAtom } from '@/service/userService';
import { prepareTicketListWithoutNoticeGroup } from './CustomerListInProjectHandler';
import { getProjectListUserIsB2C, updateProjectDetail } from '@/service/projectService';
import trI18n from '@/common/utils/trI18n';
import { buildSnackbarMessage } from '@/component/common/SnackbarMessage';

export const addNewRowProjectCustomer = (args) => {
    let newUserData = {
        id: Date.now().toString(),
        usage: Constant.SYSTEM_USAGE_TYPE.USER,
        affiliationOrganization: args.corporationId,
        mailAddress: '',
        status: Constant.STATUS_OF_USER.WAITING,
        firstName: '',
        lastName: '',
        phoneNumber: '',
        organizationName: '',
        isAddNew: true
    }

    let currentUserAddList = cloneDeep(args.userAddList);
    args.setUserAddList([newUserData, ...currentUserAddList]);
}   

export const isEmptyUserAddList = argsAll => {
    let count = 0;
    const userAddLists = cloneDeep(argsAll.userAddList);
    for (const user of userAddLists) {
        if (
            Utils.isEmptyString(user.mailAddress) &&
            Utils.isEmptyString(user.firstName) &&
            Utils.isEmptyString(user.lastName) &&
            (Utils.isEmptyString(user.phoneNumber) || user.phoneNumber === Constant.DEFAULT_COUNTRY_CODE) &&
            Utils.isEmptyString(user.organizationName)
        ) {
            count += 1;
        }
    }
    return count === userAddLists.length;
};

export const isChangeDataEdited = argsAll => {
    let statusFlag = false;
    for (const customer of argsAll.customerList) {
        // Only check customers in edit mode
        if (!customer.isEditMode) {
            continue;
        }

        // Skip if origin values haven't been set yet (safety check)
        if (!customer.originFirstName || !customer.originLastName) {
            continue;
        }

        if (
            !Utils.isNotChangedName(
                Constant.LANGUAGE_TYPE.JP,
                customer.firstName,
                customer.originFirstName
            ) ||
            !Utils.isNotChangedName(
                Constant.LANGUAGE_TYPE.JP,
                customer.lastName,
                customer.originLastName
            ) ||
            (customer.tel || '') !== (customer.originPhoneNumber || '') ||
            (customer.organizationName || '') !== (customer.originOrganizationName || '')
        ) {
            statusFlag = true;
            break;
        }
    }
    return statusFlag;
};

export const isDisplayPopupCancelAddOrEditChecker = argsAll => {
    if (!isEmptyUserAddList(argsAll) || isChangeDataEdited(argsAll)) {
        return true;
    }
    return false;
};

export const onClickCancelEdit = argsAll => {
    if (isDisplayPopupCancelAddOrEditChecker(argsAll)) {
        return argsAll.setIsOpenModalConfirmCancel(true);
    }
    argsAll.setUserAddList([]);
    argsAll.setIsEditMode(false);
    let newCustomerList = argsAll.customerList.map(customer => {
        if (customer.isEditMode) {
            delete customer.isEditMode;
            customer.firstName = customer.originFirstName;
            customer.lastName = customer.originLastName;
            customer.tel = customer.originPhoneNumber || '';
            customer.organizationName = customer.originOrganizationName || '';
        }
        return customer;
    });
    argsAll.setCustomerList(newCustomerList);
    return;
};

export const isEmptyErrorEditUsers = argsAll => {
    let statusFlag = true;
    if (Utils.isEmptyObject(argsAll.errorEditUser)) {
        return statusFlag;
    }
    for (let user in argsAll.errorEditUser) {
        if (!Utils.isEmptyObject(argsAll.errorEditUser[user])) {
            statusFlag = false;
        }
    }
    return statusFlag;
};

export const isAddNewB2C = argAll => {
    return Utils.isEmptyArray(argAll.userAddList) ? false : true;
};

/**
 * Check if any customer in edit mode has empty required email
 * @param {Array} customerList - List of customers to check
 * @returns {boolean} - True if any customer in edit mode has empty required email
 */
const hasEmptyRequiredEmailInEditMode = customerList => {
    return customerList.some(customer => {
        if (!customer.isEditMode) return false;

        // For users from phone call, email is required but may be empty
        if (customer.isEmailFromPhoneCall) {
            const emailValue = customer.mailAddress || customer.mail || '';
            return Utils.isEmptyString(emailValue.trim());
        }
        return false;
    });
};

export const checkDisableBtnSubmit = argAll => {
    if (!isAddNewB2C(argAll) && !argAll.isEditMode) {
        argAll.setIsDisableBtnSubmit(true);
        return;
    } else if (isAddNewB2C(argAll) && !argAll.isEditMode) {
        argAll.setIsDisableBtnSubmit(!Utils.isEmptyObject(validateUserAddList(argAll.userAddList)));
        return;
    } else if (argAll.isEditMode && !isAddNewB2C(argAll)) {
        const hasEmptyRequiredEmail = hasEmptyRequiredEmailInEditMode(argAll.customerList);
        argAll.setIsDisableBtnSubmit(
            !(isChangeDataEdited(argAll) && isEmptyErrorEditUsers(argAll) && !hasEmptyRequiredEmail)
        );
        return;
    } else {
        const hasEmptyRequiredEmail = hasEmptyRequiredEmailInEditMode(argAll.customerList);
        argAll.setIsDisableBtnSubmit(
            !(
                isChangeDataEdited(argAll) &&
                isEmptyErrorEditUsers(argAll) &&
                Utils.isEmptyObject(validateUserAddList(argAll.userAddList)) &&
                !hasEmptyRequiredEmail
            )
        );
        return;
    }
};

export const validatedEmail = (email, userAddList, userId) => {
    let errors;
    // validate email
    if (Utils.isEmptyString(email.trim())) {
        errors = trI18n('COMMSG014');
    } else if (!Utils.validateEmail(email.trim())) {
        errors = trI18n('COMM01MSG07');
    } else if (email.trim().length > Constant.EMAIL_MAX_LENGTH) {
        errors = trI18n('COMMSG027').replace('{{maxLength}}', Constant.EMAIL_MAX_LENGTH);
    } else if(email.trim().length < 4) {
        errors = trI18n('COMMSG026').replace('{{minLength}}', 4);
    } 
    else {
        let emails = userAddList.filter(x => {
            const userEmail = (x?.mailAddress || '').trim().toLowerCase();
            return userEmail === email.trim().toLowerCase() && x.id !== userId;
        });
        let isExistEmail = emails && emails.length > 0 ? true : false;
        if (isExistEmail) {
            errors = trI18n('USER09MSG007');
        } else {
            errors = '';
        }
    }

    return errors;
};

export const validatedNameUser = name => {
    let errors;
    if (Utils.isEmptyString(name.trim())) {
        errors = trI18n('COMMSG014');
    } else if (name.trim().length > 200) {
        errors = trI18n('COMMSG027').replace('{{maxLength}}', '200');
    } else {
        errors = '';
    }

    return errors;
};

export const validatedPhoneNumber = phoneNumber => {
    let errors;
    phoneNumber = phoneNumber?.trim();

    if (Utils.isEmptyString(phoneNumber)) {
        errors = '';
    } else if (Utils.isValidPhoneNumber(phoneNumber)) {
        errors = trI18n('COMMSG027').replace('{{maxLength}}', '20');
    }

    return errors;
};

export const validatedOrganizationName = organizationName => {
    let errors;
    if (Utils.isEmptyString(organizationName?.trim())) {
        errors = '';
    } else if (organizationName?.trim().length > 200) {
        errors = trI18n('COMMSG027').replace('{{maxLength}}', '200');
    } else {
        errors = '';
    }

    return errors;
};

export const findMailDuplicate = (userAddList) => {
    let valueArr = userAddList.map(item => item.mailAddress.trim().toLowerCase()).filter(item => !!item);
    const mailDuplicate = valueArr.filter((item, idx) => valueArr.indexOf(item) !== idx);
    return mailDuplicate;
}

export const findPhoneDuplicate = (userAddList) => {
    let valueArr = userAddList.map(item => item.phoneNumber?.trim()).filter(item => !!item);
    const phoneDuplicate = valueArr.filter((item, idx) => valueArr.indexOf(item) !== idx);
    return phoneDuplicate;
}

export const handleTextFieldAddChange = (event, userId, args) => {
    let currentAddList = cloneDeep(args.userAddList);
    let errorAddList = args.errorAddList;
    let inputValue = event.target.value;
    let errorsRow = errorAddList[`${userId}`] ? errorAddList[`${userId}`] : {};
    const index = Utils.findIndexInArrayById(userId, currentAddList);
    let errors = '';
    switch (event.target.id) {
        case `mailAddress-${userId}`:
            errors = validatedEmail(inputValue, currentAddList, userId)
            if(!Utils.isEmptyString(errors)) {
                errorsRow.email = errors;
                errorAddList[`${userId}`] = errorsRow;
                args.setErrorAddList(errorAddList);

                currentAddList[index].mailAddress = inputValue;
            }
            else {
                delete errorsRow.email ;
                errorAddList[`${userId}`] = errorsRow;
                args.setErrorAddList(errorAddList);
                
                currentAddList[index].mailAddress = inputValue;
            }
            break;
        case `firstName-${userId}`:
            errors = validatedNameUser(inputValue);

            if (!Utils.isEmptyString(errors)) {
                errorsRow.firstName = errors;
                errorAddList[`${userId}`] = errorsRow;
                args.setErrorAddList(errorAddList);
        
            } else {
                delete errorsRow.firstName;
                errorAddList[`${userId}`] = errorsRow;
                args.setErrorAddList(errorAddList);
        
            }
            currentAddList[index].firstName = inputValue;
            break;
        case `lastName-${userId}`:
            errors = validatedNameUser(inputValue);

            if (!Utils.isEmptyString(errors)) {
                errorsRow.lastName = errors;
                errorAddList[`${userId}`] = errorsRow;
                args.setErrorAddList(errorAddList);
        
            } else {
                delete errorsRow.lastName;
                errorAddList[`${userId}`] = errorsRow;
                args.setErrorAddList(errorAddList);
        
            }
            currentAddList[index].lastName = inputValue;
            break;
        case `organizationName-${userId}`:
            errors = validatedOrganizationName(inputValue);

            if (!Utils.isEmptyString(errors)) {
                errorsRow.organizationName = errors;
                errorAddList[`${userId}`] = errorsRow;
                args.setErrorAddList(errorAddList);
            } else {
                delete errorsRow.organizationName;
                errorAddList[`${userId}`] = errorsRow;
                args.setErrorAddList(errorAddList);
            }
            currentAddList[index].organizationName = inputValue;
            break;
        default:
            return;
    }
    args.setUserAddList(currentAddList);

    let mailDuplicate = findMailDuplicate(currentAddList);
    currentAddList.forEach(item => {
        if (!mailDuplicate.includes(item.mailAddress.trim().toLowerCase()) && errorAddList[`${item.id}`]?.email === trI18n('USER09MSG007')) {
            delete errorAddList[`${item.id}`]?.email;
            args.setErrorAddList(errorAddList);
        }
    });
    
}

export const handlePhoneNumberChange = (phoneNumber, userId, args) => {
    let currentAddList = cloneDeep(args.userAddList);
    let errorAddList = args.errorAddList;
    let errorsRow = errorAddList[`${userId}`] ? errorAddList[`${userId}`] : {};
    const index = Utils.findIndexInArrayById(userId, currentAddList);
    
    let errors = validatedPhoneNumber(phoneNumber);
    if (!Utils.isEmptyString(errors)) {
        errorsRow.phoneNumber = errors;
        errorAddList[`${userId}`] = errorsRow;
        args.setErrorAddList(errorAddList);
    } else {
        delete errorsRow.phoneNumber;
        errorAddList[`${userId}`] = errorsRow;
        args.setErrorAddList(errorAddList);
    }
    
    currentAddList[index].phoneNumber = phoneNumber || '';
    args.setUserAddList(currentAddList);

    let phoneDuplicate = findPhoneDuplicate(currentAddList);
    
    // Set errors cho TẤT CẢ items có phone duplicate (từ phần tử thứ 2 trở đi)
    phoneDuplicate.forEach(duplicatePhone => {
        let itemsWithThisPhone = currentAddList.filter(item => 
            item.phoneNumber?.trim() === duplicatePhone
        );
        
        // Skip phần tử đầu tiên, set error cho các phần tử từ thứ 2 trở đi
        itemsWithThisPhone.forEach((item, idx) => {
            if (idx > 0) { // Từ phần tử thứ 2
                if (!errorAddList[`${item.id}`]) {
                    errorAddList[`${item.id}`] = {};
                }
                errorAddList[`${item.id}`].phoneNumber = trI18n('USER09MSG011');
            }
        });
    });
    
    // Clear errors cho items không còn duplicate hoặc là phần tử đầu tiên
    currentAddList.forEach(item => {
        const isDuplicate = phoneDuplicate.includes(item.phoneNumber?.trim());
        const isFirstOccurrence = isDuplicate && 
            currentAddList.findIndex(u => u.phoneNumber?.trim() === item.phoneNumber?.trim()) === 
            currentAddList.findIndex(u => u.id === item.id);
        
        if ((!isDuplicate || isFirstOccurrence) && 
            errorAddList[`${item.id}`]?.phoneNumber === trI18n('USER09MSG011')) {
            delete errorAddList[`${item.id}`]?.phoneNumber;
        }
    });
    
    args.setErrorAddList({...errorAddList}); // Trigger re-render with new object
}

export const onDeleteAddUser = (userId, args) => {
    let currentAddList = cloneDeep(args.userAddList);
    currentAddList = currentAddList.filter((e, i) => e.id !== userId);
    args.setUserAddList(currentAddList);
}

export const validateUserAddList = (userAddList) => {
    if (Utils.isEmptyArray(userAddList)) return;

    let errors = {};
    userAddList.forEach(user => {
        errors[`${user.id}`] = {};

        let errorLastName = validatedNameUser(user.lastName);
        if (!Utils.isEmptyString(errorLastName)) {
            errors[`${user.id}`].lastName = errorLastName;
        } else {
            delete errors[`${user.id}`].lastName;
        }

        let errorFirstName = validatedNameUser(user.firstName);
        if (!Utils.isEmptyString(errorFirstName)) {
            errors[`${user.id}`].firstName = errorFirstName;
        } else {
            delete errors[`${user.id}`].firstName;
        }

        let errorEmail = validatedEmail(user.mailAddress, userAddList, user.id);
        if (!Utils.isEmptyString(errorEmail)) {
            errors[`${user.id}`].email = errorEmail;
        } else {
            delete errors[`${user.id}`].email;
        }

        let errorPhoneNumber = validatedPhoneNumber(user.phoneNumber);
        if (!Utils.isEmptyString(errorPhoneNumber)) {
            errors[`${user.id}`].phoneNumber = errorPhoneNumber;
        } else {
            delete errors[`${user.id}`].phoneNumber;
        }

        let errorOrganizationName = validatedOrganizationName(user.organizationName);
        if (!Utils.isEmptyString(errorOrganizationName)) {
            errors[`${user.id}`].organizationName = errorOrganizationName;
        } else {
            delete errors[`${user.id}`].organizationName;
        }
    });

    // Check phone duplicates
    let phoneDuplicate = findPhoneDuplicate(userAddList);
    userAddList.forEach(user => {
        if (phoneDuplicate.includes(user.phoneNumber?.trim()) && !Utils.isEmptyString(user.phoneNumber?.trim())) {
            if (!errors[`${user.id}`]) {
                errors[`${user.id}`] = {};
            }
            errors[`${user.id}`].phoneNumber = trI18n('USER09MSG011');
        }
    });

    let errorList = {};
    for(let key in errors) {
        if(!Utils.isEmptyObject(errors[key])) {
            errorList[key] = errors[key];
        }
    }
    
    return errorList;
}

export const reMoveRowProjectUser = (argsAll, id, userAddList, setUserAddList) => {
    let errorsList = argsAll.errorAddList;
    let currentAddList = cloneDeep(userAddList);

    currentAddList = currentAddList.filter((e, i) => e.id !== id);
    let mailDuplicate = findMailDuplicate(currentAddList);
    let phoneDuplicate = findPhoneDuplicate(currentAddList);
    let hasErrorChanged = false;

    currentAddList.forEach(item => {
        if (
            !mailDuplicate.includes(item.mailAddress.trim().toLowerCase()) &&
            !Utils.isEmptyString(errorsList[`${item.id}`]?.email)
        ) {
            delete errorsList[`${item.id}`]?.email;
            hasErrorChanged = true;
        }

        if (
            !phoneDuplicate.includes(item.phoneNumber?.trim()) &&
            !Utils.isEmptyString(errorsList[`${item.id}`]?.phoneNumber)
        ) {
            delete errorsList[`${item.id}`]?.phoneNumber;
            hasErrorChanged = true;
        }
    });

    if (hasErrorChanged) {
        argsAll.setErrorAddList(errorsList);
    }

    setUserAddList(currentAddList);
};


export const onSubmitCancelEdit = (args) => {
    let userAddList = cloneDeep(args.userAddList);
    let newCustomerList = cloneDeep(args.customerList);
    userAddList = userAddList.map((item, index) => {
        return {
            ...item,
            usage: Constant.SYSTEM_USAGE_TYPE.USER,
            affiliationOrganization: args.corporationId,
            mailAddress: '',
            status: Constant.STATUS_OF_USER.WAITING,
            firstName: '',
            lastName: '',
            phoneNumber: '+81',
            organizationName: '',
            isAutocomplete: false
        }
    })
    
    newCustomerList = newCustomerList.map(customer => {
        if (customer.isEditMode) {
            delete customer.isEditMode;
            customer.firstName = customer.originFirstName;
            customer.lastName = customer.originLastName;
            customer.tel = customer.originPhoneNumber || '';
            customer.organizationName = customer.originOrganizationName || '';
        }
        return customer;
    });
    args.setIsEditMode(false);
    args.setCustomerList(newCustomerList);
    args.setErrorAddList([]);
    args.setUserAddList(userAddList);
    args.setErrorEditUser([]);
}

export const isFillUserData = (list, id) => {
    try {
        let listClone = cloneDeep(list);
        const indexOfItem = Utils.findIndexInArrayById(id, list);

        let status = false;

        for (let key in listClone[indexOfItem]) {
            if (['mailAddress', 'firstName', 'lastName', 'phoneNumber', 'organizationName'].includes(key)) {
                if (typeof listClone[indexOfItem][key] !== 'string') {
                    throw new Error('List fields contain some elements which are not string type');
                }
                status = status | !Utils.isEmptyString(listClone[indexOfItem][key]);
            }
        }
        return status;
    } catch (err) {
        console.error({ PopupDeleteItem: err });
        return true;
    }
};

export const handleSubmit = async args => {
    // arr user add list 
    let userAddList = cloneDeep(args.userAddList);
    let errors = validateUserAddList(userAddList);
    args.setErrorAddList(errors);

    if(!Utils.isEmptyArray(userAddList)){
        userAddList = userAddList?.map(addUserInfo => {
            return {
                ...addUserInfo,
                mailAddress: addUserInfo.mailAddress?.trim().toLowerCase(),
                privilege: '',
                userType: Constant.USER_TYPE.CUSTOMER,
                firstName: addUserInfo.firstName?.trim(),
                lastName: addUserInfo.lastName?.trim(),
                phoneNumber: addUserInfo.phoneNumber?.trim() || '',
                organizationName: addUserInfo.organizationName?.trim() || '',
                affiliationOrganization: args.corporationId
            }
        })
    };

    // arr edit user list 
    let usersEditSubmit = args.customerList.filter(
        user =>
            user.isEditMode &&
            (!Utils.isNotChangedName(
                Constant.LANGUAGE_TYPE.JP,
                user.firstName,
                user.originFirstName
            ) ||
                !Utils.isNotChangedName(
                    Constant.LANGUAGE_TYPE.JP,
                    user.lastName,
                    user.originLastName
                ) ||
                (user.tel || '') !== (user.originPhoneNumber || '') ||
                (user.organizationName || '') !== (user.originOrganizationName || ''))
    );

    let uniqueUserArray = [...userAddList, ...usersEditSubmit];

    let paramsUpdateUserList = {
        userList: JSON.stringify(uniqueUserArray),
        corporationId: args.corporationDetail.id,
        actionFlag: Constant.CUSTOMERMANAGEMENT_ACTION_FLAG.CREATE_AND_UPDATE_CUTOMER_IN_CORP,
        idOfCurrentUser: args.userDetail.id
    }

    args.setIsLoading(true);
    let result = await updateUserResource(args.API, paramsUpdateUserList);
    if (!result.status) {
        if (result?.error) {
            args.enqueueSnackbar(buildSnackbarMessage(result?.error), {
                variant: 'error',
                autoHideDuration: 3000
            });
        }
        args.setIsLoading(false);
        return;
    }

    let msgKeyNoticeSuccess = 'MSG-CM-UPDATE-DATA-SUCCESS';
    const userSuccessList = Utils.convertJSONDataToObject(result.userSuccessList);
    if(!Utils.isEmptyArray(userAddList) && result.status) {
        let addUserError = {};
        let listAddUserFail = [];

        const listUserAddFail = Utils.convertJSONDataToObject(result.userFailList);
        if (!Utils.isEmptyArray(userSuccessList) && userAddList.length === uniqueUserArray.length) {
            msgKeyNoticeSuccess = 'USER09MSG003';
        }
        if (!Utils.isEmptyArray(listUserAddFail)) {
            listUserAddFail?.forEach(userFailInfo => {
                let addUserInfo;
                
                // Find user by phone number if error is phone-related
                if (userFailInfo.errorCode === 'ADD_CUSTOMER_PHONE_INVALID' && userFailInfo.phoneNumber) {
                    addUserInfo = userAddList.find(addUserInfoItem => {
                        return addUserInfoItem?.phoneNumber?.trim() === userFailInfo?.phoneNumber?.trim();
                    });
                } else {
                    // Find user by email for other errors
                    addUserInfo = userAddList.find(addUserInfoItem => {
                        return addUserInfoItem?.mailAddress?.trim()?.toLowerCase() === userFailInfo?.email?.trim()?.toLowerCase();
                    });
                }
                
                // only push user when found - avoid pushing undefined causing empty item
                if (!Utils.isEmptyObject(addUserInfo)) {
                    listAddUserFail.push(addUserInfo);
                    // Handle email or phone errors from API
                    if (userFailInfo.errorCode === 'ADD_CUSTOMER_PHONE_INVALID') {
                        addUserError[addUserInfo.id] = {
                            phoneNumber: trI18n(userFailInfo.errorCode)
                        };
                    } else {
                        addUserError[addUserInfo.id] = {
                            email: trI18n(userFailInfo.errorCode)
                        };
                    }
                }
            });
        }
        args.setErrorAddList({ ...addUserError });
        // only reload if there are no edit users - avoid reset edit mode when there is an error in both add and edit
        if (Utils.isEmptyArray(usersEditSubmit)) {
            args.setUserAddList([...listAddUserFail]);
            args.setReloadCustomer(!args.reloadListCustomer);
        }
    }

    if(!Utils.isEmptyArray(usersEditSubmit) && result.status) {
        let editUserError = cloneDeep(args.errorEditUser) || {};
        let hasEditError = false;
        let updatedCustomerList = cloneDeep(args.customerList);
        
        const listUserEditFail = Utils.convertJSONDataToObject(result.userFailList);
        
        if (!Utils.isEmptyArray(listUserEditFail)) {
            listUserEditFail?.forEach(userFailInfo => {
                let editUserInfo;
                
                // Find user by phone number if error is phone-related
                if (userFailInfo.errorCode === 'ADD_CUSTOMER_PHONE_INVALID' && userFailInfo.phoneNumber) {
                    editUserInfo = usersEditSubmit.find(editUserInfoItem => {
                        return editUserInfoItem?.tel?.trim() === userFailInfo?.phoneNumber?.trim();
                    });
                } else {
                    // Find user by email for other errors
                    editUserInfo = usersEditSubmit.find(editUserInfoItem => {
                        return editUserInfoItem?.mailAddress?.trim()?.toLowerCase() === userFailInfo?.email?.trim()?.toLowerCase();
                    });
                }
                
                if (!Utils.isEmptyObject(editUserInfo)) {
                    hasEditError = true;
                    
                    // Initialize error object for this user if not exists
                    if (!editUserError[editUserInfo.id]) {
                        editUserError[editUserInfo.id] = {};
                    }
                    
                    // Handle email or phone errors from API
                    if (userFailInfo.errorCode === 'ADD_CUSTOMER_PHONE_INVALID') {
                        editUserError[editUserInfo.id].phoneNumber = trI18n(userFailInfo.errorCode);
                    } else {
                        editUserError[editUserInfo.id].email = trI18n(userFailInfo.errorCode);
                    }
                    
                    // Ensure failed users remain in edit mode
                    const customerIndex = updatedCustomerList.findIndex(c => c.id === editUserInfo.id);
                    if (customerIndex !== -1) {
                        updatedCustomerList[customerIndex].isEditMode = true;
                    }
                }
            });
            
            // Update state with errors
            args.setErrorEditUser({ ...editUserError });
            
            // If there are errors, stop here and let user fix them
            if (hasEditError) {
                args.setCustomerList(updatedCustomerList);
                args.setIsLoading(false);
                return;
            }
        }
        
        // All edit users succeeded - exit edit mode
        updatedCustomerList = updatedCustomerList.map(customer => {
            if (customer.isEditMode) {
                delete customer.isEditMode;
                // Update origin values for successfully edited users
                customer.originFirstName = customer.firstName;
                customer.originLastName = customer.lastName;
                customer.originPhoneNumber = customer.tel || '';
                customer.originOrganizationName = customer.organizationName || '';
            }
            return customer;
        });
        args.setCustomerList(updatedCustomerList);
        args.setIsEditMode(false);
    }

    if (
        !(
            Utils.isEmptyArray(usersEditSubmit) &&
            Utils.isEmptyArray(userSuccessList) &&
            !Utils.isEmptyArray(userAddList)
        )
    ) {
        args.setReloadCustomer(true);
        args.enqueueSnackbar(buildSnackbarMessage(msgKeyNoticeSuccess), {
            variant: 'success',
            autoHideDuration: 3000
        });

        // Update B2C user list in Recoil atom for voice call feature
        if (args.setListB2CUserListInOrg && args.listB2CUserListInOrg) {
            // Collect all successfully changed users
            let changedUsers = [];
            
            // Add successfully added users
            if (!Utils.isEmptyArray(userSuccessList)) {
                changedUsers = [...changedUsers, ...userSuccessList];
            }
            
            // Add successfully edited users
            if (!Utils.isEmptyArray(usersEditSubmit)) {
                changedUsers = [...changedUsers, ...usersEditSubmit];
            }
            
            // Update atom with changed users
            updateB2CUserListInAtom(args.listB2CUserListInOrg, changedUsers, [], args.setListB2CUserListInOrg);
        }
    }
};


/**
 * isDisableBtnDeleteB2C
 * is disable btn delete b2c user
 * @param {*} argsAll 
 * @returns 
 */
export const isDisableBtnDeleteB2C = (argsAll, user) => {
    if (!argsAll.userDetail || Utils.isEmptyObject(argsAll.userDetail) || !Utils.isAdminUser(argsAll.userDetail)) {
        return true;
    }

    if (!user || Utils.isEmptyObject(user) || user.active === Constant.ACTIVE_FLAG.DELETED) {
        return true;
    }

    return false;
}

/**
 * 
 * @param {*} isAdmin 
 * @param {*} user 
 */
export const isDisableBtnEditB2C = (isAdmin, user) => {
    return !isAdmin || user.active === Constant.ACTIVE_FLAG.DELETED
}


/**
 * onClickDeleteB2C
 * @param {*} argsAll 
 * @param {*} user 
 * @returns 
 */
export const onClickDeleteB2C = async (argsAll, user) => {
    if (!user || Utils.isEmptyObject(user)) {
        return;
    }

    argsAll.setIsOpenDialogConfirmDelete({
        isEnable: true,
        userId: user.id
    });
}


/**
 * handleDeleteB2CUser
 * @param {*} argsAll 
 * @param {*} userId 
 * @returns 
 */
export const handleDeleteB2CUser = async (argsAll, userId) => {
    if (!userId) return;
    argsAll.setIsLoading(true);

    // update users amount of corporation
    let userUpdated = {
        id: userId,
        corporationId: argsAll.corporationId,
    };

    let userListUpdate = {
        deletedUserId: userId,
        corporationId:  argsAll.corporationDetail.id,
        userList: JSON.stringify(userUpdated),
        actionFlag: Constant.USERMANAGEMENT_ACTION_FLAG.DELETE_B2C_CUSTOMER_IN_CORPORATION,
        idOfCurrentUser: argsAll.userDetail.id
    }
    
    let deleteB2CResult =  await updateUserResource(argsAll.API, userListUpdate);

    if (!deleteB2CResult) {
        argsAll.setIsLoading(false);
        return;
    }

    let getTicketListOfB2C = await getTicketListOfCustomerUser(
        argsAll.API,
        argsAll.corporationId,
        userId
    );

    // Archive ticket
    if (!Utils.isEmptyArray(getTicketListOfB2C)) {
        let allTicketOfUser = getTicketListOfB2C.filter(
            ticket =>
                ticket.public === Constant.TICKET_PUBLIC.PUBLIC &&
                ticket.liveOrArchived === Constant.LIVE_OR_ARCHIVED.LIVE &&
                [
                    Constant.TICKET_TYPE.QA,
                    Constant.TICKET_TYPE.INTERNAL_QA,
                    Constant.TICKET_TYPE.EXTERNAL_QA,
                    Constant.TICKET_TYPE.DELEGATE,
                    Constant.TICKET_TYPE.BROADCAST_PARENT,
                    Constant.TICKET_TYPE.BROADCAST_CHILD
                ].includes(ticket.ticketType)
        );
        let ticketListToArchive = await prepareTicketListWithoutNoticeGroup(argsAll.API, allTicketOfUser);
        if (!Utils.isEmptyArray(ticketListToArchive)) {
            await archiveTicketList(argsAll.API, ticketListToArchive, argsAll.userDetail.id);
        }
    }

    // Delete B2C from all project
    let projectListUserIsCustomer = await getProjectListUserIsB2C(argsAll.API, userId);

    if (projectListUserIsCustomer && projectListUserIsCustomer.relationRecord && !Utils.isEmptyArray(projectListUserIsCustomer.relationRecord)) {
        let arrayPromiseDeleteB2CFromProject = [];
        projectListUserIsCustomer.relationRecord.forEach(project => {
            let objDeleteProjectB2C = {
                id: project.id,
                active: Constant.ACTIVE_FLAG.DELETED,
                type: project.type
            }
            arrayPromiseDeleteB2CFromProject.push(updateProjectDetail(argsAll.API, project.id, objDeleteProjectB2C));
        });
        await Promise.all(arrayPromiseDeleteB2CFromProject);
    }
    
    let currentUserIndexInCustomerList = Utils.findIndexInArrayById(userId, argsAll.customerList);
    let currentCustomerList = cloneDeep(argsAll.customerList);
    currentCustomerList[currentUserIndexInCustomerList].active = Constant.ACTIVE_FLAG.DELETED;
    argsAll.setCustomerList(currentCustomerList);
    argsAll.setIsLoading(false);

    // Update B2C user list in Recoil atom for voice call feature
    if (argsAll.setListB2CUserListInOrg && argsAll.listB2CUserListInOrg) {
        updateB2CUserListInAtom(argsAll.listB2CUserListInOrg, [], [userId], argsAll.setListB2CUserListInOrg);
    }
}

export const handleEdit = (user, args) => {
    let customerList = cloneDeep(args.customerList);
    customerList = customerList.map(customer => {
        if (customer.id === user.id) {
            // Normalize the format by extracting and re-stringifying
            const currentFirstName = Utils.getTextByLanguage(
                Constant.LANGUAGE_TYPE.JP,
                customer.firstName
            );
            const currentLastName = Utils.getTextByLanguage(
                Constant.LANGUAGE_TYPE.JP,
                customer.lastName
            );
            
            // Save in consistent JSON format
            const normalizedFirstName = JSON.stringify({
                jp: {
                    A: 'F',
                    text: currentFirstName
                }
            });
            const normalizedLastName = JSON.stringify({
                jp: {
                    A: 'F',
                    text: currentLastName
                }
            });
            
            return Object.assign(customer, {
                isEditMode: true,
                firstName: normalizedFirstName,
                lastName: normalizedLastName,
                originFirstName: normalizedFirstName,
                originLastName: normalizedLastName,
                originPhoneNumber: customer.tel || '',
                originOrganizationName: customer.organizationName || ''
            });
        }
        return customer;
    });
    
    // Batch state updates to prevent multiple re-renders
    args.setCustomerList(customerList);
    args.setIsEditMode(true);
};

export const validatedNameUserEditing = (event, name) => {
    let errors;
    if (Utils.isEmptyString(name.trim())) {
        errors = trI18n('COMMSG014'); 
    } else if (name.trim().length > 200) {
        errors = trI18n('COMMSG027').replace('{{maxLength}}', '200'); 
    } else {
        errors = '';
    }

    return errors;
};

export const validateEditUser = (event, userId, args) => {
    let customerList = cloneDeep(args.customerList);
    let errorEditUser = args.errorEditUser;
    let inputValue = event.target.value;
    let errorsRow = errorEditUser[`${userId}`] ? errorEditUser[`${userId}`] : {};
    const index = Utils.findIndexInArrayById(userId, customerList);
    
    // Safety check
    if (index === -1) {
        return;
    }
    
    let errors = '';
    switch (event.target.id) {
        case `firstName-${userId}`:
            errors = validatedNameUserEditing(event, inputValue);

            if (!Utils.isEmptyString(errors)) {
                errorsRow.firstName = errors;
                errorEditUser[`${userId}`] = errorsRow;
                args.setErrorEditUser(errorEditUser);
            } else {
                delete errorsRow.firstName;
                errorEditUser[`${userId}`] = errorsRow;
                args.setErrorEditUser(errorEditUser);
            }

            // Only update if value actually changed
            const currentFirstName = Utils.getTextByLanguage(
                Constant.LANGUAGE_TYPE.JP,
                customerList[index].firstName
            );
            if (currentFirstName !== inputValue) {
                customerList[index].firstName = JSON.stringify({
                    jp: {
                        A: 'F',
                        text: inputValue
                    }
                });
            }
            break;
        case `lastName-${userId}`:
            errors = validatedNameUserEditing(event, inputValue);

            if (!Utils.isEmptyString(errors)) {
                errorsRow.lastName = errors;
                errorEditUser[`${userId}`] = errorsRow;
                args.setErrorEditUser(errorEditUser);
            } else {
                delete errorsRow.lastName;
                errorEditUser[`${userId}`] = errorsRow;
                args.setErrorEditUser(errorEditUser);
            }
            
            // Only update if value actually changed
            const currentLastName = Utils.getTextByLanguage(
                Constant.LANGUAGE_TYPE.JP,
                customerList[index].lastName
            );
            if (currentLastName !== inputValue) {
                customerList[index].lastName = JSON.stringify({
                    jp: {
                        A: 'F',
                        text: inputValue
                    }
                });
            }
            break;
        case `organizationName-${userId}`:
            errors = validatedOrganizationName(inputValue);

            if (!Utils.isEmptyString(errors)) {
                errorsRow.organizationName = errors;
                errorEditUser[`${userId}`] = errorsRow;
                args.setErrorEditUser(errorEditUser);
            } else {
                delete errorsRow.organizationName;
                errorEditUser[`${userId}`] = errorsRow;
                args.setErrorEditUser(errorEditUser);
            }
            customerList[index].organizationName = inputValue;
            break;
        case `mailAddress-${userId}`:
            errors = validatedEmail(inputValue, customerList, userId);
            if (!Utils.isEmptyString(errors)) {
                errorsRow.email = errors;
                errorEditUser[`${userId}`] = errorsRow;
                args.setErrorEditUser(errorEditUser);

                customerList[index].mailAddress = inputValue;
            } else {
                delete errorsRow.email ;
                errorEditUser[`${userId}`] = errorsRow;
                args.setErrorEditUser(errorEditUser);
                customerList[index].mailAddress = inputValue;
            }
            break;
        default:
            return;
    }
    args.setCustomerList(customerList);
};

export const handlePhoneNumberChangeEdit = (phoneNumber, userId, args) => {
    let customerList = cloneDeep(args.customerList);
    let errorEditUser = args.errorEditUser;
    let errorsRow = errorEditUser[`${userId}`] ? errorEditUser[`${userId}`] : {};
    const index = Utils.findIndexInArrayById(userId, customerList);
    
    let errors = validatedPhoneNumber(phoneNumber);
    if (!Utils.isEmptyString(errors)) {
        errorsRow.phoneNumber = errors;
        errorEditUser[`${userId}`] = errorsRow;
        args.setErrorEditUser(errorEditUser);
    } else {
        delete errorsRow.phoneNumber;
        errorEditUser[`${userId}`] = errorsRow;
        args.setErrorEditUser(errorEditUser);
    }
    
    customerList[index].tel = phoneNumber || '';
    args.setCustomerList(customerList);

    // Check for phone duplicates in edit mode
    let editingUsers = customerList.filter(user => user.isEditMode);
    let phoneListForCheck = editingUsers.map(user => ({ id: user.id, phoneNumber: user.tel }));
    let phoneDuplicate = findPhoneDuplicate(phoneListForCheck);
    
    // Set errors cho items duplicate (từ thứ 2 trở đi)
    phoneDuplicate.forEach(duplicatePhone => {
        let itemsWithThisPhone = editingUsers.filter(user => 
            user.tel?.trim() === duplicatePhone
        );
        
        itemsWithThisPhone.forEach((user, idx) => {
            if (idx > 0) {
                if (!errorEditUser[`${user.id}`]) {
                    errorEditUser[`${user.id}`] = {};
                }
                errorEditUser[`${user.id}`].phoneNumber = trI18n('USER09MSG011');
            }
        });
    });
    
    // Clear errors
    editingUsers.forEach(user => {
        const isDuplicate = phoneDuplicate.includes(user.tel?.trim());
        const isFirstOccurrence = isDuplicate && 
            editingUsers.findIndex(u => u.tel?.trim() === user.tel?.trim()) === 
            editingUsers.findIndex(u => u.id === user.id);
        
        if ((!isDuplicate || isFirstOccurrence) && 
            errorEditUser[`${user.id}`]?.phoneNumber === trI18n('USER09MSG011')) {
            delete errorEditUser[`${user.id}`]?.phoneNumber;
        }
    });
    
    args.setErrorEditUser({...errorEditUser});
};


/**
 * isDisableBtnRestoreB2C
 * @param {*} isAdmin 
 * @param {*} user 
 * @returns 
 */
export const isDisableBtnRestoreB2C = (isAdmin, user) => {
    if (!isAdmin || !user || Utils.isEmptyObject(user)) {
        return true;
    }

    if (![Constant.STATUS_OF_USER.DELETED, Constant.STATUS_OF_USER.REJECT].includes(Utils.getUserStatus(user))) {
        return true;
    }
    
    return false;
}


/**
 * handleClickRestoreB2C
 * @param {*} argsAll 
 * @param {*} user 
 */
export const handleClickRestoreB2C = (argsAll, user) => {
    if (!user || Utils.isEmptyObject(user)) {
        return;
    }

    argsAll.setSelectedUser(user);
    argsAll.setIsOpenDialogConfirmRestoreUser(true);
}


/**
 * onSubmitRestoreB2C
 * @param {*} argsAll 
 */
export const onSubmitRestoreB2C = async (argsAll) => {
    if (!argsAll || !argsAll.selectedUser || Utils.isEmptyObject(argsAll.selectedUser)) return;

    let restoreUserId = argsAll.selectedUser.id;
    let restoreB2CRequestDto = {
        userId: restoreUserId,
        type: Constant.RESTORE_USER_TYPE.B2C_IN_ORG,
        currentUserId: argsAll.userDetail.id,
        corporationId: argsAll.corporationId
    }

    argsAll.setIsLoading(true);
    let restoreResult = await handleRestoreUser(argsAll.API, restoreB2CRequestDto);
    if (!restoreResult) {
        argsAll.setIsLoading(false);
        return;
    }

    if (!restoreResult.status) {
        argsAll.setIsLoading(false);
        if (!restoreResult?.errorMessage) {
            return;
        }

        if (restoreResult?.errorMessage === Constant.DATA_CHANGED_PLEASE_RELOAD_ERROR) {
            argsAll.setIsOpenModalReloadPage(true);
        } else if (argsAll.setEditUserError){
            argsAll.setEditUserError(trI18n(restoreResult?.errorMessage));
        }
        return;
    }

    // Reload
    argsAll.setReloadCustomer(!argsAll.reloadListCustomer);
    argsAll.setIsLoading(false);
    argsAll.enqueueSnackbar(buildSnackbarMessage('MSG-CM-UPDATE-DATA-SUCCESS'), {
        variant: 'success',
        autoHideDuration: 3000
    });

    // Update B2C user list in Recoil atom for voice call feature
    if (argsAll.setListB2CUserListInOrg && argsAll.listB2CUserListInOrg && argsAll.selectedUser) {
        const restoredUser = argsAll.selectedUser;
        updateB2CUserListInAtom(argsAll.listB2CUserListInOrg, [restoredUser], [], argsAll.setListB2CUserListInOrg);
    }
}